/* eslint-disable no-useless-escape */
import { Component, Input, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify, { Config } from 'dompurify';

@Component({
  selector: 'email-html-viewer',
  template: `<div [innerHTML]="safeHtml" class="email-body"></div>`,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  styles: [
    `
      .email-body {
        pre {
          white-space: pre-wrap;
        }
      }
    `
  ]
})
export class EmailHtmlViewerComponent {
  private static hooksInstalled = false;

  /** Map like { '<image001@cid>': '/api/gmail/messages/:id/attachments/:attId' } */
  @Input() cidMap: Record<string, string> | null = null;

  safeHtml: SafeHtml;

  constructor(private sanitizer: DomSanitizer) {
    this.installHooksOnce();
  }

  @Input() set html(value: string | null) {
    const raw = value || '';
    // Some sources (pasted leads, plain-text form submissions) arrive as plain
    // text rather than HTML. Rendering that verbatim collapses the line breaks
    // into a wall of text, so convert it to line-broken HTML first. Genuine
    // email bodies always contain markup and take the pass-through path.
    const input = this.looksLikeHtml(raw) ? raw : this.textToHtml(raw);
    const preprocessed = this.preprocessHtml(input);
    const cfg: Config = {
      ADD_ATTR: ['srcset', 'sizes', 'data-src', 'data-original', 'data-srcset', 'referrerpolicy', 'decoding', 'loading'],
      // include mailto:
      ALLOWED_URI_REGEXP: /^(?:(?:https?|data|blob|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    };

    const cleaned = DOMPurify.sanitize(preprocessed, cfg);
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(cleaned);
  }

  /** Detects whether a string already contains an HTML tag. Genuine email bodies always do. */
  private looksLikeHtml(value: string): boolean {
    return /<[a-z!/][\s\S]*>/i.test(value);
  }

  /** Escapes plain text and turns line breaks into `<br>` so they survive rendering. */
  private textToHtml(text: string): string {
    if (!text) return '';

    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped.replace(/\r\n|\r|\n/g, '<br>');
  }

  private preprocessHtml(html: string): string {
    // 1) Replace cid:… with mapped URLs (before sanitize, because DOMPurify drops cid)
    if (this.cidMap && html.includes('cid:')) {
      html = html.replace(/src=["']cid:([^"']+)["']/gi, (_m, cid) => {
        // common forms: <image@cid> or image@cid (some HTML includes angle brackets in attr)
        const keyWithBrackets = `<${cid}>`;
        const url = this.cidMap![keyWithBrackets] || this.cidMap![cid];
        return url ? `src="${url}"` : `src=""`;
      });
    }

    return html;
  }

  private installHooksOnce() {
    if (EmailHtmlViewerComponent.hooksInstalled) return;

    // Make links open safely in a new tab
    DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
      if (node.tagName === 'A' && node.hasAttribute('href')) {
        node.setAttribute('target', '_blank');
        const rel = (node.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
        node.setAttribute('rel', Array.from(new Set([...rel, 'noopener', 'noreferrer'])).join(' '));
      }

      if (node.tagName === 'IMG') {
        node.setAttribute('decoding', 'async');
        node.setAttribute('loading', 'lazy');
        node.setAttribute('referrerpolicy', 'no-referrer');
      }
    });

    EmailHtmlViewerComponent.hooksInstalled = true;
  }
}
